import { KPI_STATUS } from '../constants';
import { QeScoreKpiBreakdownMetric } from '../interfaces';

export function getScoreBackgroundClassByScore(
  score: number,
  classes: Record<string, string>,
) {
  if (score >= 81) return classes.qeScoreBadgeExcellent;
  if (score >= 61) return classes.qeScoreBadgeGood;
  if (score >= 41) return classes.qeScoreBadgeFair;
  if (score >= 21) return classes.qeScoreBadgePoor;
  return classes.qeScoreBadgeVeryPoor;
}

export function getBackgroundColorByScore(score: number): string {
  if (score >= 81) return 'var(--qe-score-excellent)';
  if (score >= 61) return 'var(--qe-score-good)';
  if (score >= 41) return 'var(--qe-score-fair)';
  if (score >= 21) return 'var(--qe-score-poor)';
  return 'var(--qe-score-very-poor)';
}

export function getTextColorByScore(score: number): string {
  if (score >= 41 && score <= 60) return '#000000';
  return '#ffffff';
}

export function formatGeneratedDate(dateString: string): string {
  if (!dateString) return '';
  const datePart = dateString.split('T')[0];
  const date = new Date(`${datePart}T00:00:00`);

  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return formattedDate;
}

export function getNextDayDateString(dateString: string): string {
  if (!dateString) return '';
  const datePart = dateString.split('T')[0];
  const date = new Date(`${datePart}T00:00:00`);
  date.setDate(date.getDate() + 1);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return formatGeneratedDate(`${yyyy}-${mm}-${dd}`);
}

export function formatLastChecked(totalSeconds: number) {
  if (totalSeconds < 60) {
    return `${totalSeconds} second${totalSeconds === 1 ? '' : 's'}`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes} minute${minutes === 1 ? '' : 's'} and ${seconds} second${
    seconds === 1 ? '' : 's'
  }`;
}

export function constructLink(applicationName: string): string {
  const encodedApplicationName = encodeURIComponent(`["${applicationName}"]`);
  const encodedKpi = encodeURIComponent(
    '"Unified Quality Engineering Dashboard (Application Level)"',
  );

  const baseUrl = 'https://cbre.kaiburr.com/eburl/app/interactive-dashboards/';
  return `${baseUrl}?applicationName=${encodedApplicationName}&kpi=${encodedKpi}`;
}

export function getMaximumKpiWeight(
  kpiData: QeScoreKpiBreakdownMetric[] | undefined,
): number {
  if (!kpiData) return 0;
  return kpiData.reduce((max, metric) => {
    return metric.status.toLowerCase() === KPI_STATUS.NOT_APPLICABLE
      ? max
      : metric.weight + max;
  }, 0);
}

export function getWholePercentage(score: number, maxScore: number): string {
  if (maxScore === 0) return '0%';
  const percentage = (score / maxScore) * 100;
  return `( ${Math.round(percentage)}% )`;
}

export const scrollToBottom = () => {
  setTimeout(() => {
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth',
    });
  }, 100);
};

export function isAppNotApplicable(
  kpiData: QeScoreKpiBreakdownMetric[] | undefined,
): boolean {
  if (!kpiData) return false;
  return kpiData.every(
    metric => metric.status.toLowerCase() === KPI_STATUS.NOT_APPLICABLE,
  );
}

export function hasNoScoreData(
  trendData: { maxWeight: number }[],
  currentMonthWeight?: { maxWeight: number },
): boolean {
  const trendAllZero =
    trendData.length === 0 || trendData.every(entry => entry.maxWeight === 0);
  const currentMonthZero = (currentMonthWeight?.maxWeight ?? 0) === 0;

  return trendAllZero && currentMonthZero;
}

export function downloadBlobFile(
  blob: Blob,
  fileName: string,
  emptyFileErrorMessage: string,
) {
  if (!blob.size) {
    throw new Error(emptyFileErrorMessage);
  }

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
}
